import React, { useEffect, useRef, useState } from 'react'
import { Alert, Button, Modal } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCamera } from '@fortawesome/free-solid-svg-icons'

const PosQrScanner = ({ onScan }) => {
  const [showScanner, setShowScanner] = useState(false)
  const [cameraReady, setCameraReady] = useState(false)
  const [cameraError, setCameraError] = useState('')
  const videoRef = useRef(null)

  useEffect(() => {
    if (!showScanner) {
      return undefined
    }

    let disposed = false
    let stream
    let videoElement
    let scanTimer
    let detecting = false

    const startScanner = async () => {
      if (!window.isSecureContext) {
        setCameraError(
          'Camera access requires a secure HTTPS connection. You can still enter the QR ID manually.'
        )
        return
      }

      if (typeof window.BarcodeDetector !== 'function') {
        setCameraError(
          'QR scanning is not supported in this browser. You can still enter the QR ID manually.'
        )
        return
      }

      if (typeof navigator.mediaDevices?.getUserMedia !== 'function') {
        setCameraError(
          'Camera access is not available in this browser. You can still enter the QR ID manually.'
        )
        return
      }

      try {
        const detector = new window.BarcodeDetector({ formats: ['qr_code'] })
        stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { facingMode: { ideal: 'environment' } }
        })

        if (disposed) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }

        videoElement = videoRef.current
        if (!videoElement) {
          throw new Error('Camera preview could not be initialized.')
        }

        videoElement.srcObject = stream
        await videoElement.play()
        setCameraReady(true)

        scanTimer = window.setInterval(async () => {
          if (disposed || detecting || videoElement.readyState < 2) {
            return
          }

          detecting = true
          try {
            const results = await detector.detect(videoElement)
            const scannedValue = results.find(
              (result) => result.rawValue
            )?.rawValue

            if (scannedValue && !disposed) {
              onScan(scannedValue)
              setShowScanner(false)
            }
          } catch (error) {
            console.error('Unable to read QR code from camera:', error)
            window.clearInterval(scanTimer)
            setCameraError(
              'The QR code could not be read. Try again or enter the QR ID manually.'
            )
          } finally {
            detecting = false
          }
        }, 250)
      } catch (error) {
        console.error('Unable to start QR scanner:', error)
        stream?.getTracks().forEach((track) => track.stop())
        stream = undefined
        if (videoElement) {
          videoElement.srcObject = null
        }
        const message =
          error.name === 'NotAllowedError' ?
            'Camera permission was denied. Allow camera access or enter the QR ID manually.'
          : error.name === 'NotFoundError' ?
            'No camera was found. You can still enter the QR ID manually.'
          : `The camera could not be started${error.message ? `: ${error.message}` : '.'} You can still enter the QR ID manually.`

        setCameraError(message)
      }
    }

    setCameraReady(false)
    setCameraError('')
    startScanner()

    return () => {
      disposed = true
      window.clearInterval(scanTimer)
      stream?.getTracks().forEach((track) => track.stop())
      if (videoElement) {
        videoElement.srcObject = null
      }
    }
  }, [showScanner, onScan])

  return (
    <>
      <Button
        type='button'
        variant='outline-primary'
        className='pos-scan-qr-button mt-2'
        onClick={() => setShowScanner(true)}
      >
        <FontAwesomeIcon icon={faCamera} aria-hidden='true' /> Scan QR code
      </Button>

      <Modal
        show={showScanner}
        onHide={() => setShowScanner(false)}
        centered
        aria-labelledby='pos-qr-scanner-title'
      >
        <Modal.Header closeButton>
          <Modal.Title id='pos-qr-scanner-title'>Scan customer QR</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {cameraError ?
            <Alert variant='warning' role='status'>
              {cameraError}
            </Alert>
          : <>
              <video
                ref={videoRef}
                className='pos-qr-camera-preview'
                aria-label='Camera preview for scanning a customer QR code'
                playsInline
                muted
                hidden={!cameraReady}
              />
              <p className='mb-0' role='status' aria-live='polite'>
                {cameraReady ?
                  'Point the camera at the customer QR code.'
                : 'Starting camera…'}
              </p>
            </>
          }
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowScanner(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default PosQrScanner
